import * as React from 'react';
import type * as Api from '@ViewModels';
import type { IContentCalendarSelectedSuggestion } from '../../../models';
import { CONTENT_CALENDAR_ACCEPTED_EVENT, ContentCalendarAcceptedEvent } from './events';

/** Minimal shape of a suggestion query result this hook depends on. */
export interface IAutoSelectSuggestionQuery {
	isFetching: boolean;
	isSuccess: boolean;
	isError: boolean;
	data?: Api.IContentCalendarSuggestion[];
}

/**
 * What the hook is currently waiting to auto-select once the suggestion queries settle:
 * - `calendarId`: only suggestions belonging to that specific accepted calendar.
 * - `all-ai`: every loaded AI-generated suggestion (any truthy `calendarId`) — used when the user
 *   opts into the AI calendar via the toggle, where the exact calendar id isn't known up front.
 */
type PendingAutoSelect = { kind: 'calendarId'; calendarId: string } | { kind: 'all-ai' };

export interface IUseAutoSelectAiCalendarSuggestionsParams {
	/** The per-month suggestion query results from `useContentCalendarSuggestionFilterQueries`. */
	suggestionQueries: IAutoSelectSuggestionQuery[];
	selectSuggestion: (suggestion: IContentCalendarSelectedSuggestion) => void;
	clearSelectedSuggestions: () => void;
	/**
	 * Called when an accept event arrives, before suggestions have loaded — used to enable the
	 * suggestion toggles so the invalidated queries run and the AI suggestions become visible.
	 */
	onCalendarAccepted?: (calendarId: string) => void;
	/**
	 * Optional predicate restricting which suggestions may be auto-selected (defaults to allowing
	 * all). Pass the same campaign-type/feature filter the manual "Select All" uses so auto-select
	 * never picks suggestions for campaign types the account hasn't enabled.
	 */
	isSelectable?: (suggestion: Api.IContentCalendarSuggestion) => boolean;
	/**
	 * Fetches suggestions for months beyond those rendered on screen. A generated calendar can span
	 * more months than the visible quarter, so without this only the on-screen months would be
	 * auto-selected. When provided, NOTHING is selected until this resolves — selecting the
	 * visible months early would enable the Schedule action on a partial set, and a fast click would
	 * schedule only the on-screen months. On failure the hook falls back to selecting the visible
	 * months so the user still gets a selection (the remaining months can be selected manually).
	 */
	fetchAdditionalSuggestions?: () => Promise<Api.IContentCalendarSuggestion[]>;
}

export interface IUseAutoSelectAiCalendarSuggestionsResult {
	/**
	 * Request that all AI-generated suggestions be selected once they have loaded. Use this when the
	 * user opts into the AI calendar (e.g. confirms the AI option in the suggestions toggle modal):
	 * the caller is responsible for enabling the toggles; this hook handles the selection after the
	 * queries settle.
	 */
	requestSelectAllAiSuggestions: () => void;
}

/**
 * Auto-selects AI content calendar suggestions so the user can schedule them immediately, driven by
 * two triggers:
 * - the `content-calendar-accepted` window event (fired when a generated calendar is confirmed in
 *   the assistant drawer) — selects only that calendar's suggestions;
 * - an imperative `requestSelectAllAiSuggestions()` call (used when the user opts into AI mode via
 *   the toggle) — selects every loaded AI-generated suggestion.
 *
 * In both cases it waits for the (invalidated/newly-enabled) queries to settle, then clears any
 * existing selection and selects the matching suggestions exactly once, reusing the existing
 * selection primitives so there is no bespoke selection logic here.
 */
export function useAutoSelectAiCalendarSuggestions({
	suggestionQueries,
	selectSuggestion,
	clearSelectedSuggestions,
	onCalendarAccepted,
	isSelectable,
	fetchAdditionalSuggestions,
}: IUseAutoSelectAiCalendarSuggestionsParams): IUseAutoSelectAiCalendarSuggestionsResult {
	const [pending, setPending] = React.useState<PendingAutoSelect | null>(null);

	const onCalendarAcceptedRef = React.useRef(onCalendarAccepted);
	onCalendarAcceptedRef.current = onCalendarAccepted;
	const isSelectableRef = React.useRef(isSelectable);
	isSelectableRef.current = isSelectable;
	const selectSuggestionRef = React.useRef(selectSuggestion);
	selectSuggestionRef.current = selectSuggestion;
	const clearSelectedSuggestionsRef = React.useRef(clearSelectedSuggestions);
	clearSelectedSuggestionsRef.current = clearSelectedSuggestions;
	const fetchAdditionalSuggestionsRef = React.useRef(fetchAdditionalSuggestions);
	fetchAdditionalSuggestionsRef.current = fetchAdditionalSuggestions;
	// Identifies the most recent acted-on request so an in-flight additional-months fetch from a
	// superseded request (server-side, confirming a calendar replaces the prior AI suggestions) is
	// discarded instead of adding stale suggestions to the new selection.
	const requestSeqRef = React.useRef(0);

	const requestSelectAllAiSuggestions = React.useCallback(() => {
		setPending({ kind: 'all-ai' });
	}, []);

	// Register the window listener once for the lifetime of the calendar.
	React.useEffect(() => {
		const handleAccepted = (event: Event) => {
			const { calendarId } = (event as ContentCalendarAcceptedEvent).detail ?? {};
			if (!calendarId) {
				return;
			}
			onCalendarAcceptedRef.current?.(calendarId);
			// Last accept wins: a newer acceptance overwrites a not-yet-acted-on pending one, which is
			// correct — confirming a calendar replaces the prior AI suggestions server-side, so only the
			// most recently accepted calendar's suggestions still exist to select.
			setPending({ kind: 'calendarId', calendarId });
		};

		window.addEventListener(CONTENT_CALENDAR_ACCEPTED_EVENT, handleAccepted);
		return () => window.removeEventListener(CONTENT_CALENDAR_ACCEPTED_EVENT, handleAccepted);
	}, []);

	// Once every query has settled, select the requested AI suggestions exactly once.
	React.useEffect(() => {
		if (!pending) {
			return;
		}
		// Wait for all month queries to finish so we capture suggestions across the whole calendar.
		if (suggestionQueries.some(query => query.isFetching)) {
			return;
		}

		// Act only once EVERY query has resolved — success OR error. Acting as soon as a visible month
		// matches would rely on the additional-months fetch recovering the not-yet-resolved months,
		// which in turn relies on invalidation always preceding acceptance; waiting removes that
		// ordering dependency. Acting also drops the request, so a later unrelated refetch that
		// surfaces AI suggestions can never fire an unexpected clear-and-select.
		const allResolved =
			suggestionQueries.length > 0 && suggestionQueries.every(query => query.isSuccess || query.isError);
		if (!allResolved) {
			return;
		}

		// Honor the caller's selectability filter (e.g. enabled campaign types) so auto-select never
		// picks suggestions the manual "Select All" wouldn't.
		const matchesPending = (suggestion: Api.IContentCalendarSuggestion) => {
			const matchesRequest =
				pending.kind === 'calendarId' ? suggestion.calendarId === pending.calendarId : Boolean(suggestion.calendarId);
			return matchesRequest && (isSelectableRef.current?.(suggestion) ?? true);
		};

		const visibleMatching = suggestionQueries
			.filter(query => query.isSuccess)
			.flatMap(query => query.data ?? [])
			.filter(matchesPending);
		// `suggestionQueries` gets a fresh array identity on most renders (React Query returns new
		// arrays), so this effect re-evaluates often while a request is pending — the early returns keep
		// that cheap, and clearing the pending request makes the selection run exactly once.
		setPending(null);
		const requestSeq = ++requestSeqRef.current;

		const finishSelection = (matching: Api.IContentCalendarSuggestion[]) => {
			if (requestSeq !== requestSeqRef.current) {
				// Superseded by a newer acceptance; the prior calendar's suggestions no longer exist
				// server-side, so selecting them would be wrong.
				return;
			}
			if (matching.length === 0) {
				return;
			}
			clearSelectedSuggestionsRef.current();
			// IContentCalendarSelectedSuggestion extends IContentCalendarSuggestion with optional UI
			// fields only, so suggestions returned by the query are safe to widen here.
			matching.forEach(suggestion => selectSuggestionRef.current(suggestion as IContentCalendarSelectedSuggestion));
		};

		const fetchAdditional = fetchAdditionalSuggestionsRef.current;
		if (!fetchAdditional) {
			finishSelection(visibleMatching);
			return;
		}

		// A generated calendar can span months beyond those shown on screen. Select everything
		// in ONE step once the additional months arrive: selecting the visible months early would
		// enable the Schedule action on a partial set, and a fast click would schedule only the
		// on-screen months while the late additions get silently dropped.
		fetchAdditional()
			.then(additional => {
				// The additional scan can overlap the visible months — dedupe by id so each suggestion is
				// selected once (the selection context also dedupes, but don't rely on it here).
				const visibleIds = new Set(visibleMatching.map(suggestion => suggestion.id));
				const additionalMatching = additional
					.filter(matchesPending)
					.filter(suggestion => !visibleIds.has(suggestion.id));
				finishSelection([...visibleMatching, ...additionalMatching]);
			})
			.catch(() => {
				// Fall back to the visible months; the user can still select the remaining months manually.
				finishSelection(visibleMatching);
			});
	}, [pending, suggestionQueries]);

	return { requestSelectAllAiSuggestions };
}

/**
 * The month starts (consecutive, day 1, local time) the auto-select additional-months fetch scans.
 * Anchored at the start of the CURRENT month — not the viewed month — so accepting a generated
 * calendar while the calendar UI is paged forward still covers the calendar's earliest months.
 */
export function getAutoSelectMonths(totalMonths: number, now: Date = new Date()): Date[] {
	return Array.from({ length: totalMonths }, (_, i) => new Date(now.getFullYear(), now.getMonth() + i, 1));
}
